import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { authService } from "@/services/auth.service";
import axios from "axios";
import type { User, LoginRequest } from "@/types";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isManager: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  requiresPasswordChange: boolean;
  completeInitialPassword: (newPassword: string) => Promise<void>;
  cancelPasswordChange: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [passwordChangeToken, setPasswordChangeToken] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void authService.restoreSession().then(currentUser => {
      if (active) { setUser(currentUser); setIsLoading(false); }
    });
    return () => { active = false; };
  }, []);

  const login = async (credentials: LoginRequest) => {
    setUser(null);
    setPasswordChangeToken(null);
    const response = await authService.login(credentials);
    if (response.requiresPasswordChange && response.passwordChangeToken)
      setPasswordChangeToken(response.passwordChangeToken);
    else setUser(response.user);
  };

  const completeInitialPassword = async (newPassword: string) => {
    if (!passwordChangeToken) throw new Error("Log opnieuw in om je wachtwoord in te stellen.");
    try {
      const response = await authService.changeInitialPassword(passwordChangeToken, newPassword);
      setPasswordChangeToken(null);
      setUser(response.user);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) setPasswordChangeToken(null);
      throw error;
    }
  };

  const logout = () => {
    setPasswordChangeToken(null);
    authService.logout();
    setUser(null);
  };

  const roles = (() => {
    if (!user) return [] as string[];
    const rawRoles = (user as any)?.roles;
    if (Array.isArray(rawRoles)) {
      return rawRoles;
    }
    const singleRole = (user as any)?.role;
    return singleRole ? [singleRole] : [];
  })();

  const isAdmin = roles.includes("admin");
  const isManager = isAdmin || roles.includes("manager");

  const value = {
    user,
    isAuthenticated: !!user,
    isManager,
    isAdmin,
    isLoading,
    login,
    requiresPasswordChange: passwordChangeToken !== null,
    completeInitialPassword,
    cancelPasswordChange: () => setPasswordChangeToken(null),
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
